import { Alert, Flex, Heading, List, Text, View } from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ExternalService } from "../../../../services/ExternalService";
import { ShareCodeService } from "../../../../services/ShareCodeService";
import { Card } from "../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";
import { CANVAS, EMBEDDED, EXTERNAL, QUALITATIVE } from "../../Plan/utils";
import ConfirmedModal from "../../Rater/ConfirmedModal";
import AssessmentChart from "./Displays/AssessmentChart";
import CanvasJuriedAssessmentChart from "./Displays/CanvasJuriedAssessmentChart";
import EmbeddedDashboardResults from "./Displays/EmbeddedDashboardResults";
import ExternalJuriedAssessmentChart from "./Displays/ExternalJuriedAssessmentChart";
import Findings from "./Displays/Findings";
import FlexibleAssessmentChart from "./Displays/FlexibleAssessmentChart/FlexibleAssessmentChart";
import QualitativeMeasure from "./Displays/QualitativeMeasure";
import Recommendations from "./Displays/Recommendations";
import FilterTray from "./FilterTray";

const H2_THEME_OVERRIDE = { h2FontWeight: 700 };

const ResultsReport = () => {
  const { t } = useTranslation();
  const { planId } = useParams();
  const showNotification = useNotification();
  const [shareCodeError, setShareCodeError] = useState(false);
  const [shareCodeId, setShareCodeId] = useState("");
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [user, setUser] = useState("");

  const [plan, setPlan] = useState("");
  const [header, setHeader] = useState("");
  const [mission, setMission] = useState("");
  const [cycle, setCycle] = useState("");

  const [filters, setFilters] = useState({
    mode: "term",
    masteryResults: "true",
  });
  const [prevFilters, setPrevFilters] = useState({
    mode: "term",
    masteryResults: "true",
  });
  const [triggerMap, setTriggerMap] = useState(true);

  useEffect(() => {
    async function fetchShareCode() {
      try {
        const shareCode = await ShareCodeService.getPlanShareCode(planId);
        if (shareCode.private) {
          setShareCodeId(shareCode.private);
          setOpenConfirmModal(true);
        } else {
          throw new Error("share code not found");
        }
      } catch {
        setShareCodeError(true);
      }
    }
    fetchShareCode();
  }, [planId]);

  useEffect(() => {
    const fetchPageData = async (planId) => {
      try {
        const headerPromise = ExternalService.getFileContent(
          "response.html",
          "header",
          planId
        );
        const missionPromise = ExternalService.getFileContent(
          "response.html",
          "mission",
          planId
        );
        const planPromise = ExternalService.getAssessmentPlanById(planId);
        const cyclesPromise = ExternalService.getCycles();
        const evidencePromise = ExternalService.getAssignmentsByPlanId(planId);

        const [header, mission, plan, cycles, evidence] = await Promise.all([
          headerPromise,
          missionPromise,
          planPromise,
          cyclesPromise,
          evidencePromise,
        ]);
        setHeader(header.content);
        setMission(mission.content);

        const cycle = cycles.find((cycle) => cycle.id === plan.cycle.id);
        setCycle(cycle);

        plan.reviewers = plan.reviewers.replace(/,/g, ", ");
        plan.sections = plan.sections.sort((a, b) => a.sequence - b.sequence);

        const updatedSections = await Promise.all(
          plan.sections.map(async (section) => {
            section.reviewers = section.reviewers.replace(/,/g, ", ");

            const updatedAssessments = await Promise.all(
              section.assessments.map(async (measure) => {
                const updatedMeasure = { ...measure };

                if (measure.assessment_type !== "flexible") {
                  const description = await ExternalService.getFileContent(
                    "response.html",
                    "assignmentDescription",
                    measure.id
                  );
                  updatedMeasure.description = description.content;
                }

                const assignment_evidence = evidence.filter(
                  (assignment_evidence) =>
                    assignment_evidence.assessment_id === measure.id
                );

                updatedMeasure.assignment_evidence = assignment_evidence
                  .filter(
                    (evidence) =>
                      !isNaN(evidence.id) ||
                      (isNaN(evidence.id) && measure.purl_type !== "juried")
                  )
                  .map((evidence) => evidence.id);

                updatedMeasure.ja_assignment_evidence = assignment_evidence
                  .filter((evidence) => isNaN(evidence.id))
                  .map((evidence) => evidence.id);

                updatedMeasure.type =
                  measure.assessment_type === EXTERNAL ? EXTERNAL : CANVAS;

                return updatedMeasure;
              })
            );

            const updatedQualitativeMeasures = section.qualitative_measures.map(
              (measure) => ({
                ...measure,
                assignees: measure.assignees.replace(/,/g, ", "),
                type: QUALITATIVE,
              })
            );

            const updatedEmbeddedDatalinks = section.embedded_datalinks.map(
              (measure) => ({ ...measure, type: EMBEDDED })
            );

            // Combine and sort measures
            const updatedMeasures = [
              ...updatedAssessments,
              ...updatedQualitativeMeasures,
              ...updatedEmbeddedDatalinks,
            ].sort((a, b) => a.sequence - b.sequence);

            return { ...section, measures: updatedMeasures };
          })
        );

        plan.sections = updatedSections;

        setPlan(plan);
        setOpenConfirmModal(false);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("assessment.assessmentPlan"),
          }),
          ERROR
        );
      }
    };
    if (user) {
      fetchPageData(planId);
    }
  }, [planId, user, showNotification]);

  return (
    <>
      {shareCodeError && (
        <Alert variant="error">{t("results.previewUnavailable")}</Alert>
      )}
      {user && !openConfirmModal && (
        <View as="div" background="primary" position="relative">
          <Heading level="h2" as="h1" border="bottom" margin="0 0 small 0">
            {t("results.forCycle")} {cycle.name}
          </Heading>
          <FilterTray
            filters={filters}
            setFilters={setFilters}
            prevFilters={prevFilters}
            setPrevFilters={setPrevFilters}
            setTriggerMap={setTriggerMap}
          />
          <Flex gap="large" wrap="wrap" margin="small 0 0 0">
            {header && (
              <Flex.Item margin="small 0 0 0">
                <RichTextViewer
                  ariaLabel={t("assessment.header")}
                  value={header}
                />
              </Flex.Item>
            )}
            {mission && (
              <Flex.Item size="100%">
                <Heading
                  level="h2"
                  border="bottom"
                  themeOverride={H2_THEME_OVERRIDE}
                >
                  {t("assessment.mission")}:
                </Heading>
                <View display="block" margin="small medium 0 large">
                  <RichTextViewer
                    ariaLabel={t("assessment.mission")}
                    value={mission}
                  />
                </View>
              </Flex.Item>
            )}
            {plan.include_goals_objs && (
              <Flex.Item size="100%">
                <Heading
                  level="h2"
                  border="bottom"
                  themeOverride={H2_THEME_OVERRIDE}
                >
                  {t("dashboard.goalsAndObjectives")}:
                </Heading>
                <List isUnstyled margin="small 0 0 large">
                  {cycle.goals.map((goal) => (
                    <List.Item key={goal.id} margin="0">
                      <Text weight="bold">{goal.description}</Text>
                      <List>
                        {goal.objectives.map((objective) => (
                          <List.Item key={objective.id}>
                            {objective.description}
                          </List.Item>
                        ))}
                      </List>
                    </List.Item>
                  ))}
                </List>
              </Flex.Item>
            )}
            {plan.sections.map((section) => (
              <Flex.Item key={section.id} size="100%">
                <Flex wrap="wrap" gap="small">
                  <Flex.Item shouldGrow>
                    <Heading
                      level="h2"
                      themeOverride={H2_THEME_OVERRIDE}
                      border="bottom"
                    >
                      {section.name}
                    </Heading>
                  </Flex.Item>
                  <Flex.Item size="100%" padding="0 0 0 large">
                    <RichTextViewer
                      ariaLabel={t("assessment.sections.sectionDesc")}
                      value={section.description}
                    />
                  </Flex.Item>
                  {section.measures.map((measure, measureKey) => (
                    <Flex.Item
                      key={measureKey}
                      size="100%"
                      padding="0 0 small large"
                    >
                      {(measure.type === CANVAS ||
                        measure.type === EXTERNAL) && (
                        <>
                          {measure.assessment_type !== "flexible" &&
                            !measure.collecting_evidence && (
                              <AssessmentChart
                                measure={measure}
                                filters={filters}
                                triggerMap={triggerMap}
                              />
                            )}
                          {measure.type === CANVAS &&
                            measure.assessment_type === "flexible" && (
                              <FlexibleAssessmentChart
                                measure={measure}
                                filters={filters}
                                prevFilters={prevFilters}
                                triggerMap={triggerMap}
                              />
                            )}
                          {measure.type === CANVAS &&
                            measure.collecting_evidence && (
                              <CanvasJuriedAssessmentChart
                                measure={measure}
                                filters={filters}
                                triggerMap={triggerMap}
                                isPreview
                              />
                            )}
                          {measure.type === EXTERNAL &&
                            measure.collecting_evidence && (
                              <ExternalJuriedAssessmentChart
                                measure={measure}
                                filters={filters}
                                triggerMap={triggerMap}
                                isPreview
                              />
                            )}
                        </>
                      )}
                      {measure.type === QUALITATIVE && (
                        <QualitativeMeasure
                          measure={measure}
                          permission={false}
                          isPreview
                        />
                      )}
                      {measure.type === EMBEDDED && (
                        <EmbeddedDashboardResults
                          measure={measure}
                          planId={plan.id}
                          sectionId={section.id}
                          publicResults
                        />
                      )}
                    </Flex.Item>
                  ))}
                  <Flex.Item size="100%" padding="0 0 0 large">
                    <Findings
                      id={section.id}
                      label={
                        <Heading level="h3">
                          {t("results.sectionFindings")}
                        </Heading>
                      }
                      permission={false}
                    />
                  </Flex.Item>
                  <Flex.Item size="100%" padding="0 0 0 large">
                    <Recommendations
                      id={section.id}
                      label={
                        <Heading level="h3">
                          {t("results.sectionRecommendations")}
                        </Heading>
                      }
                      permission={false}
                    />
                  </Flex.Item>
                </Flex>
              </Flex.Item>
            ))}
          </Flex>
          <div
            style={{
              borderTop: "1px dashed #ccc",
              marginBottom: 10,
              marginTop: 20,
            }}
          />
          <Card style={{ backgroundColor: "#F1F1F1", height: "auto" }}>
            <Flex wrap="wrap" gap="small">
              <Flex.Item size="100%">
                <Findings
                  id={cycle.id}
                  label={
                    <Heading level="h3">{t("results.cycleFindings")}</Heading>
                  }
                  permission={false}
                />
              </Flex.Item>
              <Flex.Item size="100%">
                <Recommendations
                  id={cycle.id}
                  label={
                    <Heading level="h3">
                      {t("results.cycleRecommendations")}
                    </Heading>
                  }
                  permission={false}
                />
              </Flex.Item>
            </Flex>
          </Card>
        </View>
      )}
      {shareCodeId && (
        <ConfirmedModal
          open={openConfirmModal}
          setUser={setUser}
          shareCode={shareCodeId}
        />
      )}
    </>
  );
};

export default ResultsReport;
